import Page from '../../components/Page'
import { Modal, Tag, Typography, Descriptions, Image, Statistic, Input, Space, Button, Popconfirm, Tooltip, Checkbox, Result, Badge } from 'antd'
import { useTranslation } from 'react-i18next'
import Table from '../../components/Table'
import { message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../../api'
import { validateFiles, readFilesAsDataUrls, chunkImages } from '../../utils/imageBatch'
import { formatStatus } from '../../utils/serviceStatus'
import ServiceChatPanel from './ServiceChatPanel'
import { RotateLeftOutlined, RotateRightOutlined, SwapOutlined, UndoOutlined, ZoomInOutlined, ZoomOutOutlined, UploadOutlined } from '@ant-design/icons'
import CountUp from 'react-countup'
const Title = Typography.Title
const { TextArea } = Input

const moneyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
})

// HOR-75/HOR-74: statusColors/formatStatus liegen jetzt in utils/serviceStatus,
// weil die Kundendetailansicht dieselben Vorgaenge rendert.

const errorResultStatus = errorType => (errorType === 'notFound' ? '404' : errorType === 'forbidden' ? '403' : errorType === 'network' ? 'warning' : 'error')

const errorResultTitle = errorType =>
  errorType === 'notFound'
    ? i18n('errors.notFound')
    : errorType === 'forbidden'
      ? i18n('errors.noPermission')
      : errorType === 'network'
        ? i18n('errors.network')
        : i18n('errors.loadFailed')

// placeholder for soft-deleted watches/customers – may wrap on small screens, no fixed width
const deletedTag = key => <Tag style={{ whiteSpace: 'normal' }}>{i18n(key)}</Tag>

// HOR-16: binding unread formula from the API contract – GET /watchServices delivers
// lastCustomerMessageAt/adminLastReadAt (ISO|null) per process
const hasUnreadMessages = service =>
  service?.lastCustomerMessageAt != null &&
  (service.adminLastReadAt == null || new Date(service.lastCustomerMessageAt).getTime() > new Date(service.adminLastReadAt).getTime())

// HOR-80: Quellstatus des Admin-Abbruchs (Vertrag POST /watchServices/cancel). Liegt die Uhr bei
// Horoluma (zweite Gruppe) geht es nach 'cancelledAndShipping', sonst direkt nach 'finished'.
const CANCEL_DIRECT_STATUSES = ['kvaReceived', 'customerApprovedKVA', 'shippingClarification']
const CANCEL_RETURN_STATUSES = ['offerReceived', 'offerApproved', 'inProgress', 'approval', 'approvalDeclined', 'waitingForPayment']
const CANCELLABLE_STATUSES = [...CANCEL_DIRECT_STATUSES, ...CANCEL_RETURN_STATUSES]

// HOR-50/83: Kategorien des Servicekatalogs in fachlicher Reihenfolge (wie im Kunden-Wizard);
// 'other' faengt Snapshot-Eintraege ohne Kategorie auf.
const CATEGORY_ORDER = ['general', 'restauration', 'dial', 'optional']

// Alt-Anfragen (vor dem Katalog) tragen ihre Auswahl in vier Enum-Arrays; die Labels liegen
// unter serviceOptions.* (dieselben Schluessel wie in der Kunden-App, HOR-79).
const LEGACY_SERVICE_GROUPS = [
  { category: 'general', field: 'generalServices', prefix: 'service' },
  { category: 'restauration', field: 'restaurationServices', prefix: 'restauration' },
  { category: 'dial', field: 'dialServices', prefix: 'dial' },
  { category: 'optional', field: 'optionalServices', prefix: 'optional_items' },
]

const legacyServiceKeys = service => LEGACY_SERVICE_GROUPS.flatMap(({ field }) => (Array.isArray(service?.[field]) ? service[field] : []))

/**
 * HOR-50/91: Vom Kunden gewaehlte Leistungen, gruppiert nach Kategorie. Primaer der
 * requestedArticles-Snapshot ({ id, name, nameEn, category }), Fallback die vier Alt-Enums.
 * Rueckgabe { legacy, groups: [{ category, items: string[] }] } – nur nicht-leere Gruppen.
 */
const requestedServiceGroups = (service, isEnglish) => {
  const requested = Array.isArray(service?.requestedArticles) ? service.requestedArticles : []

  if (requested.length > 0) {
    const groups = [...CATEGORY_ORDER, 'other'].map(category => ({
      category,
      items: requested
        .filter(article => (CATEGORY_ORDER.includes(article?.category) ? article.category : 'other') === category)
        .map(article => (isEnglish && article?.nameEn ? article.nameEn : article?.name || '')),
    }))

    return { legacy: false, groups: groups.filter(group => group.items.length > 0) }
  }

  const groups = LEGACY_SERVICE_GROUPS.map(({ category, field, prefix }) => ({
    category,
    items: (Array.isArray(service?.[field]) ? service[field] : []).map(key => i18n(`serviceOptions.${prefix}.${key}`, { defaultValue: key })),
  }))

  return { legacy: true, groups: groups.filter(group => group.items.length > 0) }
}

// HOR-91: Anzahl der gewaehlten Leistungen – Katalog-Snapshot, sonst Summe der vier Alt-Arrays
const requestedServiceCount = service => {
  const requested = Array.isArray(service?.requestedArticles) ? service.requestedArticles : []

  return requested.length > 0 ? requested.length : legacyServiceKeys(service).length
}

// HOR-57/88: einzeilige Textzelle mit Tooltip fuer render-Spalten – der Wrapper-Default
// `ellipsis` greift nur noch bei Spalten ohne render (siehe components/Table)
const clipped = value =>
  value === null || value === undefined || value === '' ? null : (
    <Typography.Text style={{ width: '100%' }} ellipsis={{ tooltip: value }}>
      {value}
    </Typography.Text>
  )

export default () => {
  const { i18n: i18nInstance } = useTranslation()
  // HOR-50: englische Artikelnamen (nameEn) fuer Admins mit englischer Spracheinstellung
  const isEnglish = String(i18nInstance?.language || '').startsWith('en')
  const [searchParams] = useSearchParams()
  const [messageApi, contextHolder] = message.useMessage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [watchServiceId, setWatchServiceId] = useState(null)
  const [watchService, setWatchService] = useState(null)
  const [refresh, setRefresh] = useState()
  const [images, setImages] = useState([])

  // HOR-93: Positionen werden vom Parent zusammen mit dem Detail geladen (loadPositions) und
  // sind die EINZIGE Quelle fuer Tabelle UND Summen. Die Tabelle laeuft im kontrollierten
  // Modus des Wrappers und ruft nach Speichern/Loeschen loadPositions erneut auf.
  const [positions, setPositions] = useState([])
  const [positionsLoading, setPositionsLoading] = useState(false)
  const [articles, setArticles] = useState([])

  // HOR-80: Abbruch-Dialog mit Pflichtgrund
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  // HOR-7/HOR-8: image writes go through dedicated endpoints – serialize uploads/deletes via loading flags
  const [imageUploading, setImageUploading] = useState(false)
  const [imageDeleting, setImageDeleting] = useState(false)

  // HOR-10: multi-upload bookkeeping – { done, total } while a batch is running, plus the
  // items of failed chunks offered for a targeted retry (server-rejected items are excluded).
  // failedImages is BOUND to the service the batch was started for ({ serviceId, items } | null)
  // so a retry can never run against a different service (QA B1, defense in depth).
  const [uploadProgress, setUploadProgress] = useState(null)
  const [failedImages, setFailedImages] = useState(null)

  // HOR-13/HOR-14: identifier (= endpoint path) of the status action currently in flight – guards
  // against double submits and drives disabled/loading on all status action buttons
  const [pendingAction, setPendingAction] = useState(null)

  // HOR-19: local drafts for taxRate/discount – typing only edits the draft, the value is
  // validated and persisted on blur/Enter (no PATCH per keystroke, no silent NaN → 0 anymore)
  const [taxRateDraft, setTaxRateDraft] = useState('')
  const [discountDraft, setDiscountDraft] = useState('')

  // HOR-20: bumped whenever a process detail is (re)loaded so the article dropdown is
  // refetched and no longer offers articles deleted in the meantime
  const [articlesReloadKey, setArticlesReloadKey] = useState(0)

  const [pageError, setPageError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [detailError, setDetailError] = useState(null)
  const [positionsError, setPositionsError] = useState(null)
  const [rawData, setRawData] = useState([])

  const requestedServiceIdRef = useRef(null)
  // HOR-93: laufende Nummer der Positions-Ladevorgaenge – nur die juengste Antwort zaehlt
  const positionsLoadSeqRef = useRef(0)
  // HOR-92: Deep Link ?open=<id> genau einmal auswerten
  const deepLinkHandledRef = useRef(false)

  const showModal = () => {
    setIsModalOpen(true)
  }

  const fileUpload = useRef(null)

  // HOR-10 (QA minor 6): SYNCHRONOUS re-entrancy guard for the whole upload pipeline including
  // the file-reading phase. The imageUploading state alone is not enough: two quickly fired
  // events could both observe imageUploading === false while the first batch is still reading
  // files, since the state update only lands with the next render.
  const uploadBusyRef = useRef(false)

  // HOR-16: opening the detail marks the message thread as read (team-wide) – refetch the table
  // on close so the unread indicator disappears without a manual reload
  const closeModal = () => {
    setIsModalOpen(false)

    // HOR-93: ab jetzt gehoert keine laufende Antwort (Detail, Positionen, Upload, PATCH) mehr
    // zu einem sichtbaren Vorgang – alle Stale-Guards vergleichen gegen diesen Ref
    requestedServiceIdRef.current = null

    // HOR-80: ein offener Abbruch-Dialog gehoert zum Vorgang, der gerade geschlossen wird
    setCancelModalOpen(false)
    setCancelReason('')

    if (typeof refresh === 'function') refresh()
  }

  /**
   * HOR-93: Detail-State erst NACH der Schliess-Animation leeren – sofort in closeModal wuerde
   * der Inhalt waehrend des Ausblendens wegspringen. Wurde in der Zwischenzeit schon ein
   * neuer Vorgang angefordert (requestedServiceIdRef gesetzt), gehoert der State bereits ihm.
   * Zusammen mit destroyOnHidden gibt es damit keine ueberlebende Tabellen-/Chat-Instanz mehr,
   * die beim naechsten Oeffnen fremde Zeilen oder Summen 0,00 zeigen koennte.
   */
  const handleAfterClose = () => {
    if (requestedServiceIdRef.current !== null) return

    setWatchService(null)
    setWatchServiceId(null)
    setImages([])
    setPositions([])
    setPositionsError(null)
    setPositionsLoading(false)
    setDetailError(null)
  }

  const handleOk = () => {
    closeModal()
  }

  const handleCancel = () => {
    // HOR-10: drop batch upload state – it refers to the service that is being closed
    setFailedImages(null)
    setUploadProgress(null)

    closeModal()
  }

  useEffect(() => {
    let cancelled = false

    apiFetch('/articles').then(result => {
      if (cancelled) return

      if (!result.ok) {
        // articles only feed the article column enum – degrade gracefully instead of blocking the page
        messageApi.open({
          type: 'warning',
          content: i18n('errors.loadFailed'),
        })
        return
      }

      if (Array.isArray(result.data)) setArticles(result.data)
    })

    return () => {
      cancelled = true
    }
  }, [reloadKey, articlesReloadKey])

  // HOR-19: draft representation of a persisted rate value (null/undefined → empty input)
  const toDraft = value => (value === null || value === undefined ? '' : String(value))

  /**
   * HOR-93: Positionen eines Vorgangs laden – EINE Quelle fuer Tabelle und Summen.
   * Stale-Guards: (1) requestedServiceIdRef – inzwischen wurde ein anderer Vorgang geoeffnet
   * oder das Modal geschlossen; (2) positionsLoadSeqRef – ein juengerer Ladevorgang fuer
   * denselben Vorgang laeuft bereits (z.B. Speichern + Retry). Beides verwirft die Antwort
   * still, ohne positions/positionsError anzufassen. Defensiv werden zusaetzlich Zeilen
   * verworfen, deren watchService-Relation nicht zu `id` passt.
   */
  const loadPositions = async id => {
    // Review ADM-1: Stale-Check synchron VOR setPositionsLoading(true). Der Table-Wrapper ruft
    // onReload nach einem laufenden Save/Delete auch dann, wenn der Admin das Modal laengst
    // geschlossen und einen anderen Vorgang geoeffnet hat – dieser Aufruf darf den Ladezustand
    // des sichtbaren Vorgangs nicht anfassen, sonst bleibt dessen Tabelle mit Spinner gesperrt.
    if (!Number.isInteger(id) || id <= 0 || requestedServiceIdRef.current !== id) return

    const seq = ++positionsLoadSeqRef.current

    setPositionsLoading(true)

    const result = await apiFetch(`/watchServicePositions/${id}`)

    if (requestedServiceIdRef.current !== id || positionsLoadSeqRef.current !== seq) return

    setPositionsLoading(false)

    if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

    if (!result.ok) {
      setPositions([])
      setPositionsError(result.errorType || 'server')

      return
    }

    const raw = Array.isArray(result.data) ? result.data : []
    const own = raw.filter(position => !position?.watchService || position.watchService.id === id)

    if (own.length !== raw.length) console.warn(`HOR-93: ${raw.length - own.length} Position(en) mit fremder Vorgangs-ID verworfen (Vorgang ${id})`)

    setPositionsError(null)
    setPositions(own)
  }

  const loadWatchService = async id => {
    requestedServiceIdRef.current = id

    setWatchService(null)
    setWatchServiceId(null)
    setPositions([])
    setPositionsError(null)
    setDetailError(null)

    // HOR-10: batch upload state belongs to the previously opened service
    setFailedImages(null)
    setUploadProgress(null)

    // HOR-93: Detail und Positionen parallel – loadPositions bringt eigene Stale-Guards mit
    const [result] = await Promise.all([apiFetch(`/watchServices/${id}`), loadPositions(id)])

    // discard stale responses: another watch service was requested in the meantime
    if (requestedServiceIdRef.current !== id) return

    if (!result.ok || !result.data) {
      // unknown or (soft-)deleted process – retrying will not help, show a dedicated 404 result
      setDetailError(result.status === 404 ? 'notFound' : result.errorType || 'server')
      return
    }

    setWatchService(result.data)
    setImages(result.data.images || [])
    setWatchServiceId(result.data.id)

    // HOR-20: refresh the article dropdown on every (re)opened process detail
    setArticlesReloadKey(key => key + 1)

    // HOR-19: (re-)initialize the commit-on-blur drafts from the persisted values on every
    // (re)load so the inputs never show stale text from a previously opened service
    setTaxRateDraft(toDraft(result.data.taxRate))
    setDiscountDraft(toDraft(result.data.discount))
  }

  /**
   * HOR-92: Deep Link /services?open=<watchServiceId> (aus Benachrichtigungs-Mails) oeffnet das
   * Vorgangs-Detail direkt – unabhaengig von Listenfiltern; die Liste muss dafuer nicht geladen
   * sein (loadWatchService holt das Detail selbst, 404 -> bisheriges "nicht gefunden"-Result).
   * Der Session-Bootstrap in main.jsx laeuft VOR dem Rendern der Routen, die Query ueberlebt ihn;
   * ohne Session wandert sie als ?redirect= ueber den Login zurueck (utils/loginRedirect, F1).
   * Der Parameter wird sofort aus der URL entfernt, damit ein Reload das Modal nicht erneut oeffnet.
   */
  useEffect(() => {
    if (deepLinkHandledRef.current) return

    deepLinkHandledRef.current = true

    const raw = searchParams.get('open')

    if (raw === null) return

    window.history.replaceState(null, '', window.location.pathname)

    const id = Number(raw)

    if (!Number.isInteger(id) || id <= 0) return

    setIsModalOpen(true)
    loadWatchService(id)
  }, [])

  /**
   * HOR-19: commit a rate field (taxRate/discount) on blur or Enter. Mirrors the backend's
   * validation (finite number within [0,100]); invalid or empty input never triggers a request
   * and reverts the draft to the last persisted value instead of silently writing 0.
   */
  const commitRateField = async (field, draft, setDraft) => {
    if (!watchService) return

    const serviceId = watchService.id
    const persisted = watchService[field]
    const invalidKey = field === 'taxRate' ? 'services.modal.taxRateInvalid' : 'services.modal.discountInvalid'
    const trimmed = draft.trim()
    const value = trimmed === '' ? NaN : Number(trimmed)

    if (!Number.isFinite(value) || value < 0 || value > 100) {
      messageApi.open({ type: 'error', content: i18n(invalidKey) })
      setDraft(toDraft(persisted))
      return
    }

    if (value === persisted) {
      // unchanged – just normalize the draft (e.g. '019' or '19.' → '19'), no request
      setDraft(String(value))
      return
    }

    const result = await apiFetch('/watchServices', {
      method: 'PATCH',
      body: { id: serviceId, [field]: value },
    })

    // discard stale responses: another watch service was requested in the meantime
    if (requestedServiceIdRef.current !== serviceId) return

    if (result.ok) {
      // keep the modal's totals/statistics in sync with what was just persisted
      setWatchService(current => (current && current.id === serviceId ? { ...current, [field]: value } : current))
      setDraft(String(value))
      return
    }

    if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

    // HOR-61: Steuersatz/Rabatt sind ausserhalb von waitingForKVA/watchReceived gesperrt (409
    // ohne Schreibzugriff). Die Inputs sind lokal nur in diesen Status aktiv, ein 409 heisst
    // also: der Status ist zwischenzeitlich weitergelaufen – Draft zuruecksetzen und das
    // Detail neu laden, damit die Felder auch hier gesperrt erscheinen.
    if (result.status === 409 && result.data?.message === 'watchServiceLocked') {
      messageApi.open({ type: 'error', content: i18n('services.rateLock.locked') })
      setDraft(toDraft(persisted))
      await loadWatchService(serviceId)

      return
    }

    // 400 bodies carry class-validator codes as an ARRAY in data.message
    // (e.g. ['taxRateOutOfRange'] / ['discountOutOfRange'] or the @IsNumber message). We only
    // send id + this field, so any 400 means this value was rejected → field-specific message.
    const rejectedAsInvalid = result.status === 400

    messageApi.open({
      type: 'error',
      content: i18n(rejectedAsInvalid ? invalidKey : result.errorType === 'network' ? 'errors.network' : 'common.error'),
    })
    setDraft(toDraft(persisted))
  }

  /**
   * HOR-10: run an upload pipeline section under the synchronous busy guard. Only one pipeline
   * (reading + uploading) can be in flight at any time, so the finally block never clobbers the
   * flags of a concurrent batch – there is none. setUploadProgress(null) is also safe when the
   * batch aborted as stale (QA B1): loadWatchService already reset it for the newly opened
   * service, so re-setting null is a no-op; setImageUploading(false) is correct regardless of
   * the active service because after this finally no upload is in flight anymore.
   */
  const withUploadGuard = async fn => {
    if (uploadBusyRef.current) return

    uploadBusyRef.current = true
    setImageUploading(true)

    try {
      await fn()
    } finally {
      uploadBusyRef.current = false
      setImageUploading(false)
      setUploadProgress(null)
    }
  }

  /**
   * HOR-7/HOR-8 + HOR-10: dedicated image upload endpoint, multi-select since HOR-10. Images
   * are NEVER written via PATCH /watchServices anymore (the backend answers 400
   * 'imagesNotAllowed'). No optimistic state update – the grid is only updated with the
   * complete stored inventory the backend returns on 201.
   *
   * Input handler: filters/reads the selected files (no compression in the admin), derives the
   * image type ONCE from the current status and stamps it on every item, so a later retry still
   * uploads with the type the batch was started with even if the status changed in the meantime.
   * The guard is held during the reading phase too (QA minor 6), so no second batch can start
   * while files are still being read.
   */
  const handleImageUpload = async event => {
    const files = Array.from(event.target.files || [])

    // allow re-selecting the same files after an error
    event.target.value = ''

    if (!files.length || !watchService || uploadBusyRef.current) return

    // QA B1: freeze the target service NOW – everything downstream uses this id, never the
    // render-state, so a service switch mid-pipeline can be detected and can not redirect uploads
    const serviceId = watchService.id
    const type = watchService.status === 'inProgress' || watchService.status === 'approvalDeclined' ? 'final' : 'package'

    await withUploadGuard(async () => {
      const { valid, skippedInvalidType, skippedTooLarge } = validateFiles(files)
      const { items, skippedInvalidType: unreadable } = await readFilesAsDataUrls(valid)
      const skippedType = skippedInvalidType + unreadable

      if (skippedType > 0) {
        messageApi.open({ type: 'warning', content: i18n('services.modal.images.skippedInvalidType', { count: skippedType }) })
      }

      if (skippedTooLarge > 0) {
        messageApi.open({ type: 'warning', content: i18n('services.modal.images.skippedTooLarge', { count: skippedTooLarge }) })
      }

      if (!items.length) return

      await runUploadBatch(
        serviceId,
        items.map(item => ({ ...item, type })),
      )
    })
  }

  /**
   * HOR-10 (QA B1, defense in depth): retry ONLY the failed items and ONLY against the service
   * they were read for. failedImages is additionally cleared on every loadWatchService and on
   * modal close, so a stale set for another service should never exist in the first place.
   */
  const retryFailedUploads = async () => {
    const failed = failedImages

    if (!failed?.items?.length || uploadBusyRef.current) return

    if (requestedServiceIdRef.current !== failed.serviceId) {
      // the modal shows another service by now – drop the foreign retry set instead of uploading it
      setFailedImages(null)

      return
    }

    await withUploadGuard(() => runUploadBatch(failed.serviceId, failed.items))
  }

  // HOR-10: map a failed upload response to the matching message key. 400 bodies may carry the
  // error code either as a plain string or as a class-validator array (custom messages on the
  // AddWatchServiceImagesDto constraints). Nested DTO codes are path-prefixed (e.g.
  // 'images.0.invalidImageType'), so match known codes by substring – the more specific codes
  // come first because 'invalidImage' is a substring of them.
  const uploadErrorKey = result => {
    const knownCodes = ['invalidImageType', 'invalidImageCount', 'invalidImage', 'imageTooLarge']
    const backendMessage = Array.isArray(result.data?.message)
      ? result.data.message
          .map(String)
          .map(m => knownCodes.find(code => m.includes(code)))
          .find(Boolean)
      : result.data?.message

    return result.errorType === 'network'
      ? 'errors.network'
      : backendMessage === 'invalidImage'
        ? 'services.modal.images.invalidImage'
        : backendMessage === 'imageTooLarge'
          ? 'services.modal.images.imageTooLarge'
          : backendMessage === 'invalidImageType'
            ? 'services.modal.images.invalidImageType'
            : backendMessage === 'invalidImageCount'
              ? 'services.modal.images.invalidImageCount'
              : result.status === 404
                ? 'errors.notFound'
                : 'services.modal.images.uploadFailed'
  }

  // HOR-10: uploads are atomic per chunk – a 400 means nothing from this chunk was stored. If
  // the backend names the offending image (direct `index` field or a class-validator path
  // prefix 'images.<n>.<code>'), that index identifies the item to EXCLUDE from the retry set:
  // retrying it would deterministically fail again.
  const rejectedChunkIndex = result => {
    if (result.status !== 400) return null

    if (Number.isInteger(result.data?.index)) return result.data.index

    if (Array.isArray(result.data?.message)) {
      for (const raw of result.data.message) {
        const match = String(raw).match(/images\.(\d+)\./)

        if (match) return Number(match[1])
      }
    }

    return null
  }

  /**
   * HOR-10 core batch loop: upload prepared items ({ name, dataUrl, type }) in sequential
   * chunks (≤ 10 images and a bounded base64 budget per request, see utils/imageBatch) against
   * the FROZEN serviceId. Assumes the caller holds the upload guard (withUploadGuard).
   *
   * QA B1 staleness guard (same pattern as requestedServiceIdRef in loadWatchService): if
   * another service is opened mid-batch, requestedServiceIdRef moves on and the loop aborts
   * SILENTLY before every state write, message and further chunk – no images, progress, retry
   * items or messages may ever reach the wrong service's modal.
   *
   * A failed chunk never aborts the series – its retryable items are collected in failedImages
   * (bound to serviceId) for the retry button. No duplicates are possible: every 201 replaces
   * the grid with the complete stored inventory the backend returns, and a 400 chunk stored
   * nothing (atomic per request).
   */
  const runUploadBatch = async (serviceId, items) => {
    if (!items.length || requestedServiceIdRef.current !== serviceId) return

    setFailedImages(null)

    const total = items.length
    const failed = []
    let done = 0

    setUploadProgress({ done, total })

    for (const chunk of chunkImages(items)) {
      const result = await apiFetch(`/watchServices/${serviceId}/images`, {
        method: 'POST',
        body: { images: chunk.map(item => ({ data: item.dataUrl, type: item.type })) },
      })

      if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

      // QA B1: re-check after the await – another service may have been opened during the
      // request; from here on the batch is stale and must not touch state or show messages
      if (requestedServiceIdRef.current !== serviceId) return

      if (result.ok && Array.isArray(result.data?.images)) {
        setImages(result.data.images)
        done += chunk.length
        setUploadProgress({ done, total })

        continue
      }

      const guilty = rejectedChunkIndex(result)

      if (guilty !== null && guilty >= 0 && guilty < chunk.length) {
        messageApi.open({ type: 'error', content: i18n('services.modal.images.rejectedByServer', { name: chunk[guilty].name }) })
        failed.push(...chunk.filter((_, index) => index !== guilty))
      } else {
        messageApi.open({ type: 'error', content: i18n(uploadErrorKey(result)) })
        failed.push(...chunk)
      }
      // continue with the next chunk – one failed request must not abort the whole batch
    }

    // QA B1: the loop only reaches this point non-stale (checked after every await), but keep
    // the completion writes explicit about the service they belong to
    if (requestedServiceIdRef.current !== serviceId) return

    if (failed.length > 0) setFailedImages({ serviceId, items: failed })

    if (done === total) {
      messageApi.open({ type: 'success', content: i18n('services.modal.images.uploadSuccessCount', { count: done }) })
    } else {
      messageApi.open({ type: 'warning', content: i18n('services.modal.images.partialSuccess', { done, total }) })
    }
  }

  /**
   * HOR-66: API-Kennung eines Bildes fuer DELETE /watchServices/:id/images/:imageId.
   * Das Read-Shape liefert `id` = S3-Key ('images/<uuid>') bzw. 'legacy:<index>' fuer
   * Alt-Eintraege ohne Key. Beim Key wird nur der uuid-Teil gesendet (bisheriger Vertrag,
   * vermeidet '%2F' im Pfad), alles andere unveraendert. Fallback fuer alte Backends ohne
   * `id`: uuid-Teil aus `data`.
   */
  const imageDeleteId = image => {
    if (typeof image.id === 'string' && image.id.length > 0) {
      return image.id.startsWith('images/') ? image.id.slice('images/'.length) : image.id
    }

    return typeof image.data === 'string' && image.data.startsWith('images/') ? image.data.split('/').pop() : null
  }

  // HOR-66: stabile React-Identitaet eines Bildeintrags (id aus dem Read-Shape, sonst Key/Index)
  const imageIdentity = (image, index) => image.id || image.data || `index:${index}`

  /**
   * HOR-7/HOR-8: dedicated image delete endpoint. Only offered for internal images (customer images
   * have no delete button), the backend's 403 'customerImageProtected' is still handled defensively.
   * HOR-66: auch Platzhalter-Eintraege ohne Bild (id 'legacy:<n>') sind loeschbar.
   */
  const handleImageDelete = async image => {
    const imageId = imageDeleteId(image)

    if (!imageId || !watchService || imageDeleting) return

    const serviceId = watchService.id

    setImageDeleting(true)

    const result = await apiFetch(`/watchServices/${serviceId}/images/${encodeURIComponent(imageId)}`, { method: 'DELETE' })

    setImageDeleting(false)

    // HOR-93: inzwischen anderer Vorgang / Modal geschlossen – nichts mehr anfassen
    if (requestedServiceIdRef.current !== serviceId) return

    if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

    if (result.ok && Array.isArray(result.data?.images)) {
      setImages(result.data.images)
      messageApi.open({ type: 'success', content: i18n('services.modal.images.deleteSuccess') })
      return
    }

    if (result.status === 404) {
      // image already gone (deleted elsewhere) – treat as success. HOR-66: 'legacy:<n>' ist
      // positionsbasiert und nach einer fremden Loeschung verschoben, deshalb die Liste vom
      // Server holen; nur wenn das scheitert, den Eintrag lokal entfernen.
      const fresh = await apiFetch(`/watchServices/${serviceId}`)

      if (requestedServiceIdRef.current !== serviceId) return

      if (fresh.ok && Array.isArray(fresh.data?.images)) setImages(fresh.data.images)
      else setImages(current => current.filter(img => img !== image))

      messageApi.open({ type: 'success', content: i18n('services.modal.images.deleteSuccess') })
      return
    }

    if (result.status === 403 && result.data?.message === 'customerImageProtected') {
      messageApi.open({ type: 'error', content: i18n('services.modal.images.customerImageProtected') })
      return
    }

    messageApi.open({
      type: 'error',
      content: i18n(result.errorType === 'network' ? 'errors.network' : 'services.modal.images.deleteFailed'),
    })
  }

  // positions may only be edited before the offer has been sent (kept in sync with the backend's 409 lock)
  const positionsEditable = watchService?.status === 'waitingForKVA' || watchService?.status === 'watchReceived'

  // HOR-10: statuses that offer the upload UI (package vs. final is derived from the status)
  const canUploadImages = ['watchReceived', 'customerApprovedKVA', 'inProgress', 'approvalDeclined'].includes(watchService?.status)

  // HOR-10 (QA B1, defense in depth): only offer retries that belong to the CURRENTLY shown
  // service – a foreign set (should not exist, it is cleared on every load/close) never renders
  const retryItems = failedImages && failedImages.serviceId === watchService?.id ? failedImages.items : []

  // HOR-6, Story 5: the backend only writes selectedPositions when the customer accepts the KVA
  // (kvaReceived → customerApprovedKVA / shippingClarification), so the selection column and the
  // "ordered scope" totals are only meaningful from customerApprovedKVA onwards. Before the
  // acceptance (waitingForKVA, kvaReceived) and while positions are re-edited after the watch
  // arrived (watchReceived, positionsEditable again) the column stays neutral.
  const selectionVisible = [
    'customerApprovedKVA',
    'shippingClarification',
    'offerReceived',
    'offerApproved',
    'offerDeclined',
    'inProgress',
    'approval',
    'approvalDeclined',
    'waitingForPayment',
    'finishedAndShipping',
    'cancelledAndShipping',
    'finished',
  ].includes(watchService?.status)
  const selectedPositionIds = watchService?.selectedPositions || []

  // HOR-50/83: vom Kunden gewaehlte Leistungen fuer den Detail-Abschnitt (leer ohne Vorgang)
  const requestedServices = requestedServiceGroups(watchService, isEnglish)

  // HOR-93: Tabellenzeilen aus `positions` – memoisiert, weil der Table-Wrapper die Referenz
  // als Effekt-Abhaengigkeit nutzt. HOR-20: articleId as STRING – rc-select matches values
  // against option values via strict Map.get, and the valueEnum option values are always
  // strings, so a numeric id would render as raw id instead of the label. The submit handlers
  // parseFloat it back into the number the backend expects.
  const positionRows = useMemo(() => positions.map(pos => ({ ...pos, articleId: pos.article ? String(pos.article.id) : null })), [positions])

  // shared 409 handling for position endpoints: show the specific lock message instead of the
  // table's generic "request failed" (returning true suppresses the generic message, the table refetches anyway)
  const handlePositionResult = result => {
    if (result.status === 409) {
      messageApi.open({
        type: 'error',
        content: i18n('services.positions.locked'),
      })

      return true
    }

    // HOR-20: the chosen articleId points to a deleted/unknown article – nothing was saved
    if (result.status === 400 && result.data?.message === 'invalidArticleReference') {
      messageApi.open({
        type: 'error',
        content: i18n('services.positions.articleUnavailable'),
      })

      return true
    }

    return result.ok
  }

  // render helpers for soft-deleted relations (HOR-3): watch === null → watch deleted, watch.user === null → customer deleted
  const watchField = value => (watchService?.watch ? value : deletedTag('services.deletedWatch'))
  const userField = value => (!watchService?.watch ? deletedTag('services.deletedWatch') : !watchService.watch.user ? deletedTag('services.deletedUser') : value)

  /**
   * Shared handler for all admin status actions (HOR-3, Story 3; hardened for HOR-13/HOR-14).
   * Responses answer 201 with { success, customerNotified } (finish additionally { invoiceCreated }).
   * `customerNotified`/`invoiceCreated` are read with optional chaining so endpoints not (yet)
   * covered by the contract (finalize, shippingCleared) and old `true`-bodies keep working.
   * `checkNotified: false` skips the notification warning (finalize never emails by design).
   *
   * `path` doubles as the action identifier for the double-submit guard (`pendingAction`).
   * Error handling (HOR-12 atomic backend guards):
   * - network → transient, keep the modal open WITHOUT refetching so the action can simply be retried
   * - 404/400 → the status changed in the meantime; keep the modal open, refetch the detail + table
   *   so the action buttons render against the new status
   *
   * HOR-80: `body` ergaenzt den Request-Body (z.B. { reason }); `validationKeys` mappt bekannte
   * 400-Validierungscodes des Backends (String oder class-validator-Array in data.message,
   * Substring-Match) auf i18n-Schluessel – solche 400er sind KEIN Statuskonflikt, das Detail
   * bleibt stehen. Rueckgabe fuer Aufrufer mit eigenem Dialog:
   * 'success' | 'validation' | 'conflict' | 'network' | 'error' | undefined (Guard/401).
   */
  const runStatusAction = async ({ path, body = {}, successKey, errorKey = 'common.error', checkNotified = true, checkInvoice = false, validationKeys = {} }) => {
    if (pendingAction) return // double-submit guard: another status action is already in flight

    setPendingAction(path)

    try {
      const result = await apiFetch(`/watchServices/${path}`, { method: 'POST', body: { id: watchService.id, ...body } })

      if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

      if (result.ok) {
        if (checkNotified && result.data?.customerNotified === false) {
          messageApi.open({
            type: 'warning',
            content: i18n('services.actions.customerNotNotified'),
          })
        } else {
          messageApi.open({
            type: 'success',
            content: i18n(successKey),
          })
        }

        if (checkInvoice && result.data?.invoiceCreated === false) {
          messageApi.open({
            type: 'warning',
            content: i18n('services.actions.invoiceNotCreated'),
          })
        }

        // HOR-93: ueber closeModal, damit laufende Antworten als stale gelten (requestedServiceIdRef)
        closeModal()

        return 'success'
      }

      if (result.errorType === 'network') {
        messageApi.open({
          type: 'error',
          content: i18n('errors.network'),
        })

        return 'network'
      }

      if (result.status === 400) {
        const codes = [result.data?.message].flat().filter(Boolean).map(String)
        const validationCode = Object.keys(validationKeys).find(code => codes.some(raw => raw.includes(code)))

        if (validationCode) {
          messageApi.open({
            type: 'error',
            content: i18n(validationKeys[validationCode]),
          })

          return 'validation'
        }
      }

      if (result.status === 404 || result.status === 400) {
        messageApi.open({
          type: 'error',
          content: i18n('services.actions.statusConflict'),
        })

        await loadWatchService(watchService.id)
        refresh()

        return 'conflict'
      }

      messageApi.open({
        type: 'error',
        content: i18n(errorKey),
      })

      refresh()

      return 'error'
    } finally {
      setPendingAction(null)
    }
  }

  /**
   * HOR-80: POST /watchServices/cancel { id, reason } ueber runStatusAction (Pending-Guard,
   * Konfliktbehandlung, Erfolgsmeldung). Validierungs-400er (cancelReasonRequired/TooLong/
   * kein HTML) lassen den Dialog offen, ebenso Netzfehler (Retry); Statuskonflikt, Erfolg und
   * sonstige Fehler schliessen ihn.
   */
  const submitCancel = async () => {
    const reason = cancelReason.trim()

    if (!reason) {
      messageApi.open({ type: 'error', content: i18n('services.cancelAction.reasonRequired') })

      return
    }

    const outcome = await runStatusAction({
      path: 'cancel',
      body: { reason },
      successKey: 'services.cancelAction.success',
      validationKeys: {
        cancelReasonTooLong: 'services.cancelAction.reasonTooLong',
        cancelReasonRequired: 'services.cancelAction.reasonRequired',
        // NoHtml-Validator antwortet mit "reason darf kein HTML enthalten"
        'kein HTML': 'services.cancelAction.reasonNoHtml',
      },
    })

    if (outcome === 'validation' || outcome === 'network' || outcome === undefined) return

    setCancelModalOpen(false)
    setCancelReason('')
  }

  const [hideDone, setHideDone] = useState(true)
  const [showOnlyImportant, setShowOnlyImportant] = useState(true)
  // HOR-16: additional, independent filter – only processes with unread customer messages
  const [showOnlyUnread, setShowOnlyUnread] = useState(false)
  // HOR-74: Kundensuche. Die Liste zeigt alle Vorgaenge, es gab aber keinen Weg,
  // die eines bestimmten Kunden zu isolieren - der Umweg ging bisher ueber Impersonate.
  const [customerQuery, setCustomerQuery] = useState('')

  const matchesCustomer = item => {
    const query = customerQuery.trim().toLowerCase()
    if (!query) return true

    const user = item.watch?.user

    // Vorgangsnummer bewusst mitdurchsucht: im Support-Fall liegt sie oft vor,
    // der Kundenname nicht (siehe Mailbetreff "Vorgangsnr. ...").
    return [user?.name, user?.email, item.number].some(value =>
      String(value || '')
        .toLowerCase()
        .includes(query),
    )
  }

  const filterItems = items =>
    items.filter(item => {
      // HOR-74: Die Kundensuche steht ueber allen anderen Filtern - wer gezielt nach
      // einem Kunden sucht, will auch dessen abgeschlossene Vorgaenge sehen.
      if (!matchesCustomer(item)) return false

      if (showOnlyUnread && !hasUnreadMessages(item)) return false

      // HOR-16: processes with unread customer messages are NEVER hidden by the done/action
      // filters – a customer can write in any status (even 'finished'), and a filtered-out row
      // would hide the badge and defeat the "no customer message goes unnoticed" goal
      if (hasUnreadMessages(item)) return true

      if (!hideDone && !showOnlyImportant) return true

      const first = item.status !== 'finished'
      const second = [
        'waitingForKVA',
        'customerApprovedKVA',
        'shippingClarification',
        'watchReceived',
        'offerApproved',
        'inProgress',
        'waitingForPayment',
        'approvalDeclined',
        // HOR-13: return shipping still has to be confirmed by the admin – action required
        'finishedAndShipping',
        'cancelledAndShipping',
      ].includes(item.status)

      if (hideDone && showOnlyImportant) return first && second
      if (hideDone) return first
      if (showOnlyImportant) return second

      return true
    })

  // HOR-74: Der Hinweis gehoert zu den Checkbox-Filtern. Bezugsgroesse ist deshalb die
  // Kundensuche-Treffermenge, sonst zaehlt er die bewusst weggesuchten Vorgaenge mit.
  const customerMatches = rawData.filter(matchesCustomer)
  const hiddenCount = customerMatches.length - filterItems(rawData).length

  const articleEnum = {}

  try {
    articles.forEach(article => (articleEnum[article.id] = article.name))
  } catch (e) {
    console.log(e)
  }

  return (
    <Page title={i18n('services.title')}>
      {contextHolder}
      {pageError && (
        <Result
          status={errorResultStatus(pageError)}
          title={errorResultTitle(pageError)}
          extra={
            <Button
              type="primary"
              onClick={() => {
                setPageError(null)
                setRawData([])
                setReloadKey(key => key + 1)
              }}
            >
              {i18n('common.retry')}
            </Button>
          }
        />
      )}
      {!pageError && (
        <>
          <div style={{ userSelect: 'none', marginBottom: 16, display: 'grid', gridGap: 16 }}>
            {/* HOR-74: Kundenfilter fuer die Vorgangsliste. */}
            <Input.Search
              allowClear
              style={{ maxWidth: 420 }}
              placeholder={i18n('services.customerFilter.placeholder')}
              value={customerQuery}
              onChange={e => setCustomerQuery(e.target.value)}
            />
            <Checkbox checked={hideDone} onChange={e => setHideDone(e.target.checked)}>
              {i18n('services.checkbox.hideDone')}
            </Checkbox>
            <Checkbox checked={showOnlyImportant} onChange={e => setShowOnlyImportant(e.target.checked)}>
              {i18n('services.checkbox.onlyActionRequired')}
            </Checkbox>
            <Checkbox checked={showOnlyUnread} onChange={e => setShowOnlyUnread(e.target.checked)}>
              {i18n('services.chat.unreadFilter')}
            </Checkbox>
            {hiddenCount > 0 && <Typography.Text type="secondary">{i18n('services.filterHint', { count: hiddenCount })}</Typography.Text>}
          </div>
          <Table
            key={reloadKey}
            filter={filterItems}
            refresh={setRefresh}
            columns={[
              {
                title: i18n('common.createdAt'),
                key: 'createdAt',
                dataIndex: 'createdAt',
                // HOR-90: Rohwert aus dem Record – Pro-Table uebergibt bei ellipsis ein React-Element
                // als erstes Argument, moment(<Element>) ergab "heute 00:00" fuer jede Zeile
                render: (_, record) => moment(record.createdAt).fromNow(),
              },
              {
                title: i18n('services.columns.number'),
                key: 'number',
                dataIndex: 'number',
                // HOR-16: unobtrusive unread indicator – dot on the process number, cleared once the
                // detail was opened (the message GET marks the thread as read)
                render: (_, record) =>
                  hasUnreadMessages(record) ? (
                    <Tooltip title={i18n('services.chat.unreadTooltip')}>
                      <Badge dot offset={[4, 0]}>
                        {record.number}
                      </Badge>
                    </Tooltip>
                  ) : (
                    record.number
                  ),
              },
              {
                title: i18n('services.columns.status'),
                key: 'status',
                width: 180,
                dataIndex: 'status',
                // HOR-88: Rohwert aus dem Record (siehe "Erstellt"); formatStatus(<Element>) lief auf
                // i18n('services.status.[object Object]') und zeigte ueberall "Unbekannt"
                render: (_, record) => {
                  const status = formatStatus(record.status)

                  return <Tag color={status.color}>{status.title}</Tag>
                },
              },
              {
                // HOR-57: Textspalten mit render bleiben ueber clipped() einzeilig mit Tooltip
                title: i18n('services.columns.email'),
                width: 180,
                key: 'email',
                dataIndex: 'email',
                render: (_, record) =>
                  !record.watch ? deletedTag('services.deletedWatch') : !record.watch.user ? deletedTag('services.deletedUser') : clipped(record.email),
              },
              {
                title: i18n('services.columns.username'),
                key: 'name',
                dataIndex: 'name',
                render: (_, record) =>
                  !record.watch ? deletedTag('services.deletedWatch') : !record.watch.user ? deletedTag('services.deletedUser') : clipped(record.name),
              },
              {
                title: i18n('services.columns.make'),
                key: 'make',
                dataIndex: 'make',
                render: (_, record) => (!record.watch ? deletedTag('services.deletedWatch') : clipped(record.make)),
              },
              {
                title: i18n('services.columns.model'),
                key: 'model',
                dataIndex: 'model',
                render: (_, record) => (!record.watch ? deletedTag('services.deletedWatch') : clipped(record.model)),
              },
              {
                title: i18n('services.columns.referenceNumber'),
                key: 'referenceNumber',
                dataIndex: 'referenceNumber',
                render: (_, record) => (!record.watch ? deletedTag('services.deletedWatch') : clipped(record.referenceNumber)),
              },
              {
                // HOR-22: Die Spalte zeigte nur eine Zahl. Ohne Vorschaubild liess sich in der
                // Uebersicht nicht erkennen, um welche Uhr es geht - jetzt Thumbnail, und wo
                // keins existiert, ein Platzhalter mit der Bezeichnung der Uhr.
                title: i18n('services.columns.images'),
                key: 'images',
                dataIndex: 'images',
                width: 150,
                ellipsis: false,
                // HOR-88: Rohwert aus dem Record statt aus dem ersten Argument (siehe "Erstellt")
                render: (_, record) => {
                  const images = Array.isArray(record.images) ? record.images : []
                  const count = images.length
                  // Kundenbilder zuerst - sie zeigen die Uhr, interne Bilder oft nur das Paket.
                  const preview = (images || []).find(image => image.url && image.source === 'customer') || (images || []).find(image => image.url)
                  const label = [record.make, record.model].filter(Boolean).join(' ') || record.referenceNumber || i18n('services.columns.noWatchLabel')

                  return (
                    <Space direction="vertical" size={4}>
                      {preview ? (
                        <Image src={preview.url} alt={label} width={96} height={64} style={{ objectFit: 'contain', background: '#f5f5f5', borderRadius: 4 }} />
                      ) : (
                        <Tooltip title={label}>
                          <div
                            style={{
                              width: 96,
                              height: 64,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 4,
                              background: '#fafafa',
                              border: '1px dashed #d9d9d9',
                              borderRadius: 4,
                              fontSize: 11,
                              lineHeight: 1.2,
                              textAlign: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {label}
                          </div>
                        </Tooltip>
                      )}
                      <Tag style={{ marginInlineEnd: 0 }}>{i18n('services.modal.imageCount', { count })}</Tag>
                    </Space>
                  )
                },
              },
              {
                title: i18n('services.columns.services'),
                key: 'services',
                dataIndex: 'services',
                // HOR-91: Anzahl aus dem Record (String(<Element>) ergab "[object Object] Leistung(en)");
                // HOR-50: Katalog-Snapshot zuerst, der Tooltip listet die gewaehlten Leistungen
                render: (_, record) => {
                  const names = requestedServiceGroups(record, isEnglish).groups.flatMap(group => group.items)
                  const tag = <Tag>{i18n('services.modal.serviceCount', { count: record.services })}</Tag>

                  if (!names.length) return tag

                  return (
                    <Tooltip
                      overlayStyle={{ maxWidth: 480 }}
                      title={
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {names.map((name, index) => (
                            <li key={index}>{name}</li>
                          ))}
                        </ul>
                      }
                    >
                      {tag}
                    </Tooltip>
                  )
                },
              },
              {
                title: i18n('services.columns.consultation'),
                key: 'consultation',
                dataIndex: 'consultation',
                width: 85,
                // HOR-88-Folgefehler: das erste Argument war immer ein (truthy) Element -> immer "Ja"
                render: (_, record) => (record.consultation ? <Tag color="green">{i18n('common.yes')}</Tag> : <Tag color="red">{i18n('common.no')}</Tag>),
              },
              {
                // HOR-77: Bemerkungen auf zwei Zeilen begrenzt (statt einzeilig abgeschnitten), der
                // volle Text steht im Tooltip. ellipsis bewusst aus, sonst setzt antd nowrap auf die td.
                title: i18n('services.columns.notes'),
                key: 'description',
                dataIndex: 'description',
                width: 220,
                ellipsis: false,
                render: (_, record) =>
                  record.description ? (
                    <Tooltip overlayStyle={{ maxWidth: 480 }} title={<span style={{ whiteSpace: 'pre-wrap' }}>{record.description}</span>}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {record.description}
                      </div>
                    </Tooltip>
                  ) : null,
              },
              {
                title: i18n('common.actions'),
                readonly: true,
                width: 74,
                key: 'actions',
                render: (_, ws) => {
                  return (
                    <>
                      <a
                        onClick={() => {
                          setIsModalOpen(true)
                          loadWatchService(ws.id)
                        }}
                      >
                        {[
                          'waitingForKVA',
                          'shippingClarification',
                          'inProgress',
                          'approvalDeclined',
                          'waitingForPayment',
                          'watchReceived',
                          'customerApprovedKVA',
                          'offerApproved',
                          // HOR-13: confirming the return shipping is an admin action, so these read "review"
                          'finishedAndShipping',
                          'cancelledAndShipping',
                        ].includes(ws.status)
                          ? i18n('services.modal.check')
                          : i18n('services.modal.info')}
                      </a>
                    </>
                  )
                },
              },
            ]}
            modes={[]}
            title={i18n('services.tableTitle')}
            request={async () => {
              const result = await apiFetch('/watchServices')

              if (!result.ok) {
                setPageError(result.errorType)
                setRawData([])

                return {
                  data: [],
                  total: 0,
                  success: false,
                }
              }

              const services = Array.isArray(result.data) ? result.data : []

              const mapped = services.map(service => ({
                ...service,
                name: service.watch?.user?.name,
                email: service.watch?.user?.email,
                make: service.watch?.referenceNumber?.make,
                model: service.watch?.referenceNumber?.model,
                referenceNumber: service.watch?.referenceNumber?.number,
                // HOR-50/91: Katalog-Snapshot (requestedArticles), Fallback Summe der vier Alt-Arrays
                services: requestedServiceCount(service),
              }))

              setRawData(mapped)

              return {
                data: mapped,
                total: mapped.length,
                success: true,
              }
            }}
          />
        </>
      )}
      <input type="file" multiple accept=".jpg,.png,.jpeg,.webp,.bmp" ref={fileUpload} onChange={handleImageUpload} style={{ display: 'none' }} />
      {/* HOR-93: destroyOnHidden – rc-dialog friert den Inhalt bei visible=false per MemoChildren ein;
          ohne Zerstoerung ueberlebten Positionstabelle und Chat-Panel das Schliessen, der null-Render
          beim naechsten Oeffnen kam nicht an (Chat pollte weiter und markierte still als gelesen,
          Zeilen des alten Vorgangs, Summen 0,00). afterClose leert den Detail-State nach der Animation. */}
      <Modal
        width={1400}
        title={i18n('services.modal.title')}
        open={isModalOpen}
        onOk={handleOk}
        footer={null}
        onCancel={handleCancel}
        destroyOnHidden
        afterClose={handleAfterClose}
      >
        {!watchService && detailError && (
          <Result
            status={errorResultStatus(detailError)}
            title={errorResultTitle(detailError)}
            extra={
              // retrying a 404 will not help – only offer retry for transient errors (HOR-1 pattern)
              detailError !== 'notFound' && (
                <Button type="primary" onClick={() => loadWatchService(requestedServiceIdRef.current)}>
                  {i18n('common.retry')}
                </Button>
              )
            }
          />
        )}
        {watchService && (
          // HOR-93: key an die Vorgangs-ID gebunden – React remountet Tabelle, Summen, Chat und die
          // defaultValue-Textareas bei jedem Vorgangswechsel, auch wenn B->A ohne Null-Commit in
          // EINEM Render reconciled wird (Browser-Task-Reihenfolge damit irrelevant).
          <div key={watchService.id}>
            {/* HOR-13/HOR-14: finalize is only allowed from these source statuses (backend answers 400 otherwise) */}
            {['companyDeclined', 'customerDeclinedKVA', 'offerDeclined', 'cancelledAndShipping', 'finishedAndShipping'].includes(watchService.status) && (
              <Popconfirm
                title={i18n('services.actions.finalize')}
                description={i18n('services.actions.finalizeConfirm')}
                okText={i18n('services.actions.finalize')}
                cancelText={i18n('common.cancel')}
                onConfirm={() => runStatusAction({ path: 'finalize', successKey: 'services.actions.finalizeSuccess', checkNotified: false })}
              >
                <Button style={{ marginBottom: 16 }} disabled={!!pendingAction} loading={pendingAction === 'finalize'}>
                  {i18n('services.actions.finalize')}
                </Button>
              </Popconfirm>
            )}
            {/* HOR-80: Abbruch durch Horoluma mit Pflichtgrund – nur aus den Status des Vertrags
                (POST /watchServices/cancel); "Vorgang beenden" bleibt fuer seine Status erhalten */}
            {CANCELLABLE_STATUSES.includes(watchService.status) && (
              <Button danger style={{ marginBottom: 16 }} disabled={!!pendingAction} loading={pendingAction === 'cancel'} onClick={() => setCancelModalOpen(true)}>
                {i18n('services.cancelAction.button')}
              </Button>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: 24 }}>
              <div>
                <Descriptions
                  layout="vertical"
                  title={i18n('services.modal.watchData')}
                  items={[
                    {
                      key: '1',
                      label: i18n('services.columns.make'),
                      children: watchField(watchService.watch?.referenceNumber?.make),
                    },
                    {
                      key: '2',
                      label: i18n('services.columns.model'),
                      children: watchField(watchService.watch?.referenceNumber?.model),
                    },
                    {
                      key: '3',
                      label: i18n('services.columns.referenceNumber'),
                      children: watchField(watchService.watch?.referenceNumber?.number),
                    },
                    {
                      key: '4',
                      label: i18n('services.modal.braceletMaterial'),
                      children: watchField(watchService.watch?.braceletMaterial),
                    },
                    {
                      key: '5',
                      label: i18n('services.modal.housingMaterial'),
                      children: watchField(watchService.watch?.housingMaterial),
                    },
                    {
                      key: '6',
                      label: i18n('services.modal.clockworkWind'),
                      children: watchField(watchService.watch?.clockworkWind),
                    },
                    {
                      key: '7',
                      label: i18n('services.modal.condition'),
                      children: watchField(getShortCondition(watchService.watch?.condition)),
                    },
                    {
                      key: '8',
                      label: i18n('services.modal.serialNumber'),
                      children: watchField(watchService.watch?.serialNumber),
                    },
                    {
                      key: '9',
                      label: i18n('services.modal.otherNotes'),
                      children: watchField(watchService.watch?.notes),
                    },
                    {
                      key: '10',
                      label: i18n('services.modal.returnAddress'),
                      children: userField(
                        [
                          watchService.watch?.user?.receiver,
                          watchService.watch?.user?.street,
                          watchService.watch?.user?.zipCode,
                          watchService.watch?.user?.city,
                          watchService.watch?.user?.country,
                        ]
                          .filter(Boolean)
                          .join(', '),
                      ),
                    },
                  ]}
                />
              </div>
              <div>
                <Title level={5}>{i18n('common.images')}</Title>
                {/* HOR-10: one row for upload, retry and progress – the retry row also renders
                    outside the upload statuses so collected failures never become unreachable */}
                {(canUploadImages || retryItems.length > 0 || uploadProgress) && (
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    {(watchService.status === 'watchReceived' || watchService.status === 'customerApprovedKVA') && (
                      <Button loading={imageUploading} disabled={imageUploading} onClick={() => fileUpload.current.click()} icon={<UploadOutlined />}>
                        {i18n('services.modal.uploadPackage')}
                      </Button>
                    )}
                    {(watchService.status === 'inProgress' || watchService.status === 'approvalDeclined') && (
                      <Button loading={imageUploading} disabled={imageUploading} onClick={() => fileUpload.current.click()} icon={<UploadOutlined />}>
                        {i18n('services.modal.uploadFinal')}
                      </Button>
                    )}
                    {retryItems.length > 0 && (
                      <Button danger disabled={imageUploading} onClick={retryFailedUploads}>
                        {i18n('services.modal.images.retryFailed', { count: retryItems.length })}
                      </Button>
                    )}
                    {uploadProgress && (
                      <Typography.Text type="secondary">
                        {i18n('services.modal.images.progress', {
                          current: Math.min(uploadProgress.done + 1, uploadProgress.total),
                          total: uploadProgress.total,
                        })}
                      </Typography.Text>
                    )}
                  </div>
                )}
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                    gridGap: 16,
                  }}
                >
                  {images.map((image, index) => {
                    // HOR-7/HOR-8: origin badge derived from source + type; the S3 key (image.data) is the stable identity
                    const badgeKey = image.source === 'customer' ? 'customerImage' : image.type === 'final' ? 'finalImage' : 'packageImage'
                    const badgeColor = image.source === 'customer' ? 'blue' : image.type === 'final' ? 'green' : 'default'

                    return (
                      <div key={imageIdentity(image, index)} style={{ display: 'grid', gridGap: 4, justifyItems: 'start' }}>
                        {!image.url && (
                          <div
                            style={{
                              height: 100,
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              border: '1px dashed #d9d9d9',
                              borderRadius: 8,
                              padding: 8,
                              textAlign: 'center',
                            }}
                          >
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {i18n('services.modal.images.imageUnavailable')}
                            </Typography.Text>
                            {/* HOR-66: Platzhalter (kein rekonstruierbarer Key, id 'legacy:<n>') waren bisher
                                unloeschbar. Nur interne Eintraege – Kundenbilder bleiben geschuetzt (403). */}
                            {image.source === 'internal' && (
                              <Popconfirm
                                title={i18n('services.brokenImage.confirm')}
                                okText={i18n('common.delete')}
                                cancelText={i18n('common.cancel')}
                                onConfirm={() => handleImageDelete(image)}
                              >
                                <Button size="small" danger loading={imageDeleting} disabled={imageDeleting}>
                                  {i18n('services.brokenImage.remove')}
                                </Button>
                              </Popconfirm>
                            )}
                          </div>
                        )}
                        {image.url && (
                          <Image
                            preview={{
                              toolbarRender: (_, { transform: { scale }, actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset } }) => (
                                <div>
                                  {image.source === 'internal' && (
                                    <div
                                      style={{
                                        textAlign: 'center',
                                        width: '100%',
                                        padding: 12,
                                        fontSize: 22,
                                        marginBottom: 16,
                                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                        color: 'white',
                                        borderRadius: 8,
                                      }}
                                    >
                                      <Button loading={imageDeleting} disabled={imageDeleting} onClick={() => handleImageDelete(image)}>
                                        {i18n('services.modal.deleteImage')}
                                      </Button>
                                    </div>
                                  )}
                                  <Space size={12} className="toolbar-wrapper">
                                    <SwapOutlined rotate={90} onClick={onFlipY} />
                                    <SwapOutlined onClick={onFlipX} />
                                    <RotateLeftOutlined onClick={onRotateLeft} />
                                    <RotateRightOutlined onClick={onRotateRight} />
                                    <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} />
                                    <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                                    <UndoOutlined onClick={onReset} />
                                  </Space>
                                </div>
                              ),
                            }}
                            style={{ height: 100, width: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            src={image.url}
                            fallback="data:image/png;base64,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"
                          />
                        )}
                        <Tag color={badgeColor} style={{ marginRight: 0 }}>
                          {i18n(`services.modal.images.${badgeKey}`)}
                        </Tag>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div>
              <Descriptions
                layout="vertical"
                title={i18n('services.modal.serviceRequest')}
                items={[
                  {
                    key: '1',
                    label: i18n('common.createdAt'),
                    children: moment(watchService.createdAt).format('LLL'),
                  },
                  {
                    key: '8',
                    label: i18n('services.modal.sentAt'),
                    children: watchService.sentAt ? moment(watchService.sentAt).format('L LT') : '',
                  },
                  {
                    key: '9',
                    label: i18n('services.modal.declinedAt'),
                    children: watchService.declinedAt ? moment(watchService.declinedAt).format('L LT') : '',
                  },
                  // HOR-59: Der Kunde kann die Ablehnung eines KVA/Angebots jetzt begruenden.
                  // Ohne Begruendung bleibt die Zeile weg, statt ein leeres Feld zu zeigen.
                  ...(watchService.customerDeclineComment
                    ? [
                        {
                          key: '9b',
                          label: i18n('services.modal.customerDeclineComment'),
                          children: <span style={{ whiteSpace: 'pre-wrap' }}>{watchService.customerDeclineComment}</span>,
                        },
                      ]
                    : []),
                  // HOR-80: Abbruch durch Horoluma – Grund und Zeitpunkt nur zeigen, wenn gesetzt
                  ...(watchService.cancelReason || watchService.cancelledAt
                    ? [
                        {
                          key: '9c',
                          label: i18n('services.cancelAction.cancelledAt'),
                          children: watchService.cancelledAt ? moment(watchService.cancelledAt).format('L LT') : i18n('common.na'),
                        },
                        {
                          key: '9d',
                          label: i18n('services.cancelAction.cancelReason'),
                          children: <span style={{ whiteSpace: 'pre-wrap' }}>{watchService.cancelReason || i18n('common.na')}</span>,
                        },
                      ]
                    : []),
                  {
                    // HOR-13/HOR-14: audit timestamps delivered by the read endpoints (ISO|null)
                    key: '16',
                    label: i18n('services.modal.paidAt'),
                    children: watchService.paidAt ? moment(watchService.paidAt).format('L LT') : '',
                  },
                  {
                    key: '17',
                    label: i18n('services.modal.completedAt'),
                    children: watchService.completedAt ? moment(watchService.completedAt).format('L LT') : '',
                  },
                  {
                    key: '2',
                    label: i18n('services.columns.status'),
                    children: <Tag color={formatStatus(watchService.status).color}>{formatStatus(watchService.status).title}</Tag>,
                  },
                  {
                    key: '5',
                    label: i18n('services.modal.consultationRequested'),
                    children: watchService.consultation ? <Tag color="green">{i18n('common.yes')}</Tag> : <Tag color="red">{i18n('common.no')}</Tag>,
                  },
                  {
                    key: '7',
                    label: i18n('services.modal.telephone'),
                    children: watchService.telephone || i18n('common.na'),
                  },
                  {
                    key: '3',
                    label: i18n('services.columns.email'),
                    children: userField(watchService.watch?.user?.email),
                  },
                  {
                    key: '4',
                    label: i18n('services.columns.username'),
                    children: userField(watchService.watch?.user?.name),
                  },
                  {
                    key: '6',
                    label: i18n('services.columns.notes'),
                    children: watchService.description,
                  },
                  {
                    key: '10',
                    label: i18n('services.modal.fullName'),
                    children: watchService.name,
                  },
                  {
                    key: '11',
                    label: i18n('services.modal.street'),
                    children: watchService.street,
                  },
                  {
                    key: '12',
                    label: i18n('services.modal.postalCode'),
                    children: watchService.postalCode,
                  },
                  {
                    key: '13',
                    label: i18n('services.modal.city'),
                    children: watchService.city,
                  },
                  {
                    key: '14',
                    label: i18n('services.modal.estimatedValue'),
                    children: watchService.worth,
                  },
                  {
                    key: '15',
                    label: i18n('services.modal.deliveryPreference'),
                    children: watchService.deliveryPreference,
                  },
                ]}
              />
            </div>
            {/* HOR-50/83: vom Kunden im Katalog gewaehlte Leistungen (requestedArticles-Snapshot),
                gruppiert nach Kategorie; Alt-Anfragen zeigen die vier Enum-Listen uebersetzt */}
            <div style={{ marginTop: 16 }}>
              <Title level={5}>{i18n('services.requestedArticles.title')}</Title>
              {requestedServices.groups.length === 0 ? (
                <Typography.Text type="secondary">{i18n('services.requestedArticles.empty')}</Typography.Text>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gridGap: 16 }}>
                  {requestedServices.groups.map(group => (
                    <div key={group.category}>
                      <Typography.Text strong>{i18n(`services.requestedArticles.categories.${group.category}`)}</Typography.Text>
                      <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                        {group.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {requestedServices.legacy && requestedServices.groups.length > 0 && (
                <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  {i18n('services.requestedArticles.legacyHint')}
                </Typography.Text>
              )}
            </div>
            <div style={{ marginTop: 16 }}>
              {positionsError && (
                <Result
                  status={errorResultStatus(positionsError)}
                  title={errorResultTitle(positionsError)}
                  extra={
                    <Button
                      type="primary"
                      loading={positionsLoading}
                      onClick={() => {
                        // HOR-93: Retry laedt ueber denselben Pfad wie das Detail (Stale-Guards inklusive)
                        setPositionsError(null)
                        loadPositions(watchService.id)
                      }}
                    >
                      {i18n('common.retry')}
                    </Button>
                  }
                />
              )}
              {!positionsError && !positionsEditable && (
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  {i18n('services.positions.lockedHint')}
                </Typography.Text>
              )}
              {!positionsError && (
                <Table
                  // HOR-93: kontrollierter Modus – Zeilen kommen aus `positions` (dieselbe Quelle wie
                  // die Summen), nach Speichern/Loeschen laedt onReload neu; key an die Vorgangs-ID
                  // gebunden, damit nie eine Tabelleninstanz einen Vorgangswechsel ueberlebt
                  key={`positions-${watchService.id}`}
                  dataSource={positionRows}
                  loading={positionsLoading}
                  onReload={() => loadPositions(watchService.id)}
                  // HOR-51: Neue Positionen starteten mit Anzahl 0 - eine Position ohne
                  // Menge ergibt fachlich keinen Sinn und die Zeile fiel im KVA mit 0,00 EUR auf.
                  newRecordDefaults={{ amount: 1 }}
                  columns={[
                    {
                      title: i18n('services.modal.posText'),
                      // HOR-20: the text is the authoritative invoice snapshot, article changes never touch it
                      tooltip: i18n('services.modal.posTextTooltip'),
                      key: 'text',
                      dataIndex: 'text',
                    },
                    {
                      // HOR-56: englischer Positionstext. Gleiche Snapshot-Semantik wie `text`;
                      // bleibt er leer, sieht auch ein englischsprachiger Kunde den deutschen Text.
                      title: i18n('services.modal.posTextEn'),
                      tooltip: i18n('services.modal.posTextEnTooltip'),
                      key: 'textEn',
                      dataIndex: 'textEn',
                    },
                    {
                      title: i18n('services.modal.posAmount'),
                      key: 'amount',
                      dataIndex: 'amount',
                      valueType: 'digit',
                    },
                    {
                      title: i18n('services.modal.posUnitPrice'),
                      key: 'price',
                      dataIndex: 'price',
                      valueType: 'digit',
                      render: (_, { price }) => {
                        return <div>{moneyFormatter.format(price)}</div>
                      },
                    },
                    {
                      title: i18n('services.modal.posTotalPrice'),
                      key: 'total',
                      dataIndex: 'total',
                      readonly: true,
                      render: (_, { price, amount }) => moneyFormatter.format(price * amount),
                    },
                    {
                      title: i18n('services.modal.posOptional'),
                      key: 'optional',
                      dataIndex: 'optional',
                      valueType: 'switch',
                      render: (_, data) => <Tag color={data.optional ? 'green' : 'yellow'}>{data.optional ? i18n('common.yes') : i18n('common.no')}</Tag>,
                    },
                    {
                      title: i18n('services.modal.posDeclined'),
                      key: 'declined',
                      dataIndex: 'declined',
                      valueType: 'switch',
                      render: (_, data) => <Tag color={data.declined ? 'red' : 'green'}>{data.declined ? i18n('common.yes') : i18n('common.no')}</Tag>,
                    },
                    {
                      // HOR-6, Story 5: readonly view of the customer's KVA selection – neutral before the acceptance
                      title: i18n('services.modal.posSelected'),
                      tooltip: i18n('services.modal.posSelectedTooltip'),
                      key: 'selected',
                      dataIndex: 'selected',
                      readonly: true,
                      render: (_, data) =>
                        !selectionVisible ? (
                          <Tag>{i18n('services.modal.posSelectedPending')}</Tag>
                        ) : data.declined ? (
                          <Tag>{i18n('services.modal.posDeclined')}</Tag>
                        ) : selectedPositionIds.includes(data.id) ? (
                          <Tag color="green">{i18n('services.modal.posSelectedYes')}</Tag>
                        ) : (
                          <Tag color="red">{i18n('services.modal.posSelectedNo')}</Tag>
                        ),
                    },
                    {
                      title: i18n('services.modal.posArticle'),
                      tooltip: i18n('services.modal.posArticleTooltip'),
                      key: 'articleId',
                      // HOR-20: without a dataIndex rc-table hands the WHOLE record to the edit form
                      // item as initial value, so saving without touching the select would send
                      // parseFloat(<object>) = NaN → null and silently detach the article relation
                      dataIndex: 'articleId',
                      valueType: 'select',
                      width: 400,
                      // HOR-20: active articles plus – only for rows referencing a soft-deleted article –
                      // a disabled entry for that id, so the edit select shows a label instead of the raw
                      // id and saving without re-selecting keeps the unchanged articleId (relation stays)
                      valueEnum: record => {
                        if (!record?.article?.deletedAt || articleEnum[record.article.id]) return articleEnum

                        return {
                          ...articleEnum,
                          [record.article.id]: {
                            text: i18n('services.positions.articleDeletedOption', { name: record.article.name }),
                            disabled: true,
                          },
                        }
                      },
                      // HOR-20: visible prefill – the server no longer copies text/price on article
                      // selection, so selecting one fills the row's text/price fields client-side
                      // (still freely editable); clearing the selection leaves text/price untouched
                      fieldProps: (form, { rowKey }) => ({
                        onChange: value => {
                          if (value === undefined || value === null) return

                          const article = articles.find(a => String(a.id) === String(value))

                          if (!article) return

                          const row = [rowKey].flat(1)

                          // GET /articles delivers price as a decimal STRING – prefill a real number,
                          // the backend rejects strings with @IsNumber (defensive pattern from Articles)
                          const price = typeof article.price === 'number' ? article.price : parseFloat(article.price)

                          form.setFieldValue([...row, 'text'], article.name)
                          // HOR-56: englische Bezeichnung mitziehen, sofern am Artikel gepflegt.
                          form.setFieldValue([...row, 'textEn'], article.nameEn || '')

                          if (Number.isFinite(price)) form.setFieldValue([...row, 'price'], price)
                        },
                      }),
                      // HOR-20: render from the position's article relation (delivered even for
                      // soft-deleted articles): null → free position, deletedAt → deleted marker
                      // HOR-57 (Review ADM-2): clipped() statt nacktem Namen – der Wrapper setzt ellipsis nur
                      // noch fuer Spalten ohne render, lange Artikelnamen sollen aber einzeilig mit Tooltip bleiben
                      render: (_, { article }) =>
                        !article ? i18n('common.na') : article.deletedAt ? deletedTag('services.positions.articleDeleted') : clipped(article.name),
                    },
                  ]}
                  modes={positionsEditable ? ['create', 'edit', 'delete'] : []}
                  title={i18n('services.modal.positions')}
                  onDelete={async position => {
                    const result = await apiFetch('/watchServicePositions', {
                      method: 'DELETE',
                      body: {
                        id: position.id,
                      },
                    })

                    return handlePositionResult(result)
                  }}
                  onCreate={async position => {
                    const result = await apiFetch('/watchServicePositions', {
                      method: 'POST',
                      body: {
                        watchServiceId,
                        // HOR-20: parse like onEdit does – the article prefill/decimal columns can
                        // yield strings, and the backend rejects non-numbers with @IsNumber
                        // HOR-51: Fallback 1 statt 0 – dieselbe Vorgabe wie newRecordDefaults.
                        amount: parseFloat(position.amount) || 1,
                        price: parseFloat(position.price) || 0,
                        text: position.text || ' ',
                        textEn: position.textEn?.trim() || null,
                        optional: !!position.optional,
                        declined: !!position.declined,
                        articleId: position.articleId ? parseFloat(position.articleId) : null,
                      },
                    })

                    // HOR-93: kein separater Refresh mehr – der Wrapper ruft nach dem Speichern onReload
                    return handlePositionResult(result)
                  }}
                  onEdit={async position => {
                    const result = await apiFetch('/watchServicePositions', {
                      method: 'PATCH',
                      body: {
                        id: position.id,
                        amount: parseFloat(position.amount),
                        price: parseFloat(position.price),
                        text: position.text,
                        textEn: position.textEn?.trim() || null,
                        optional: position.optional,
                        declined: position.declined,
                        articleId: position.articleId ? parseFloat(position.articleId) : null,
                      },
                    })

                    return handlePositionResult(result)
                  }}
                />
              )}
            </div>
            <div style={{ marginTop: 16 }}>
              <Title level={5}>{i18n('services.modal.posTotalPrice')}</Title>
              <div style={{ display: 'flex', gap: 32 }}>
                <Statistic
                  title={i18n('services.modal.totalGrossInclOpt')}
                  value={
                    ((100 - (watchService.discount || 0)) / 100) *
                    positions.map(pos => pos.amount * pos.price).reduce((a, b) => a + b, 0) *
                    (1 + watchService.taxRate / 100)
                  }
                  formatter={formatter}
                />
                <Statistic
                  title={i18n('services.modal.totalNetInclOpt')}
                  value={((100 - (watchService.discount || 0)) / 100) * positions.map(pos => pos.amount * pos.price).reduce((a, b) => a + b, 0)}
                  formatter={formatter}
                />
                <Statistic
                  title={i18n('services.modal.totalGrossExclOpt')}
                  value={
                    ((100 - (watchService.discount || 0)) / 100) *
                    positions
                      .filter(pos => !pos.optional)
                      .map(pos => pos.amount * pos.price)
                      .reduce((a, b) => a + b, 0) *
                    (1 + watchService.taxRate / 100)
                  }
                  formatter={formatter}
                />
                <Statistic
                  title={i18n('services.modal.totalNetExclOpt')}
                  value={
                    ((100 - (watchService.discount || 0)) / 100) *
                    positions
                      .filter(pos => !pos.optional)
                      .map(pos => pos.amount * pos.price)
                      .reduce((a, b) => a + b, 0)
                  }
                  formatter={formatter}
                />
                {/* HOR-6, Story 5: ordered scope – only what the customer actually selected when accepting the KVA */}
                {selectionVisible && (
                  <>
                    <Statistic
                      title={i18n('services.modal.orderedTotalGross')}
                      value={
                        ((100 - (watchService.discount || 0)) / 100) *
                        positions
                          .filter(pos => !pos.declined && selectedPositionIds.includes(pos.id))
                          .map(pos => pos.amount * pos.price)
                          .reduce((a, b) => a + b, 0) *
                        (1 + watchService.taxRate / 100)
                      }
                      formatter={formatter}
                    />
                    <Statistic
                      title={i18n('services.modal.orderedTotalNet')}
                      value={
                        ((100 - (watchService.discount || 0)) / 100) *
                        positions
                          .filter(pos => !pos.declined && selectedPositionIds.includes(pos.id))
                          .map(pos => pos.amount * pos.price)
                          .reduce((a, b) => a + b, 0)
                      }
                      formatter={formatter}
                    />
                  </>
                )}
                <div>
                  <Title level={5}>{i18n('services.modal.taxRate')}</Title>
                  {/* HOR-19: typing only edits the draft – the value is validated and persisted
                      on blur (Enter blurs, so both share one commit path, no double submit) */}
                  <Input
                    disabled={watchService.status !== 'waitingForKVA' && watchService.status !== 'watchReceived'}
                    value={taxRateDraft}
                    onChange={e => setTaxRateDraft(e.target.value)}
                    onBlur={() => commitRateField('taxRate', taxRateDraft, setTaxRateDraft)}
                    onPressEnter={e => e.target.blur()}
                  />
                </div>
                <div>
                  <Title level={5}>{i18n('services.modal.discount')}</Title>
                  <Input
                    disabled={watchService.status !== 'waitingForKVA' && watchService.status !== 'watchReceived'}
                    value={discountDraft}
                    onChange={e => setDiscountDraft(e.target.value)}
                    onBlur={() => commitRateField('discount', discountDraft, setDiscountDraft)}
                    onPressEnter={e => e.target.blur()}
                  />
                </div>
              </div>
            </div>
            {/* HOR-15/HOR-16: communication thread with the customer – own section below the positions */}
            <div style={{ marginTop: 16 }}>
              <ServiceChatPanel
                // HOR-93: explizit an die Vorgangs-ID gebunden (zusaetzlich zum key des Containers)
                key={`chat-${watchService.id}`}
                watchServiceId={watchService.id}
                active={isModalOpen}
                // HOR-3 markers: watch === null → watch deleted, watch.user === null → customer deleted –
                // in both cases the process is historical, the thread stays readable but read-only
                deleted={!watchService.watch || !watchService.watch.user}
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <Title level={5}>{i18n('services.modal.commentToCustomer')}</Title>
              <TextArea
                disabled={watchService.status !== 'waitingForKVA' && watchService.status !== 'watchReceived' && watchService.status !== 'inProgress'}
                defaultValue={watchService.notes}
                onChange={e => {
                  fetch(`${global.WatchStorage.backendUrl}/api/v1/watchServices`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${global.WatchStorage.session.token}`,
                    },
                    body: JSON.stringify({
                      id: watchService.id,
                      notes: e.target.value,
                    }),
                  })
                }}
                placeholder={i18n('services.modal.commentPlaceholder')}
                rows={4}
              />
              <Title style={{ marginTop: 16 }} level={5}>
                {i18n('services.modal.trackingInfo')}
              </Title>
              <TextArea
                defaultValue={watchService.trackingInfo}
                onChange={e => {
                  fetch(`${global.WatchStorage.backendUrl}/api/v1/watchServices`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${global.WatchStorage.session.token}`,
                    },
                    body: JSON.stringify({
                      id: watchService.id,
                      trackingInfo: e.target.value,
                    }),
                  })
                }}
                placeholder={i18n('services.modal.trackingPlaceholder')}
                rows={4}
              />
              {watchService.status === 'shippingClarification' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.shippingClearedTitle')}
                    description={i18n('services.actions.shippingClearedConfirm')}
                    okText={i18n('common.confirm')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'shippingCleared', successKey: 'services.actions.shippingClearedSuccess' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'shippingCleared'}>
                      {i18n('services.actions.shippingCleared')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {(watchService.status === 'offerApproved' || watchService.status === 'approvalDeclined') && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.inProgressTitle')}
                    description={i18n('services.actions.inProgressConfirm')}
                    okText={i18n('common.confirm')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'inProgress', successKey: 'services.actions.inProgressSuccess' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'inProgress'}>
                      {i18n('services.actions.inProgress')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {watchService.status === 'customerApprovedKVA' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.watchReceivedTitle')}
                    description={i18n('services.actions.watchReceivedConfirm')}
                    okText={i18n('common.confirm')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'watchReceived', successKey: 'services.actions.watchReceivedSuccess' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'watchReceived'}>
                      {i18n('services.actions.watchReceived')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {watchService.status === 'waitingForPayment' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.paidTitle')}
                    description={i18n('services.actions.paidConfirm')}
                    okText={i18n('services.actions.confirmPayment')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'paid', successKey: 'services.actions.paidSuccess' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'paid'}>
                      {i18n('services.actions.paid')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {/* HOR-13/HOR-14: confirm the return shipping – completes the process, emails the customer, releases the watch */}
              {(watchService.status === 'finishedAndShipping' || watchService.status === 'cancelledAndShipping') && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.shippingFinishedTitle')}
                    description={i18n('services.actions.shippingFinishedConfirm')}
                    okText={i18n('common.confirm')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'shippingFinished', successKey: 'services.actions.shippingFinishedSuccess' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'shippingFinished'}>
                      {i18n('services.actions.shippingFinished')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {watchService.status === 'waitingForKVA' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.sendKVA')}
                    description={i18n('services.actions.sendKVAConfirm')}
                    okText={i18n('common.send')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'send', successKey: 'services.actions.sendKVASuccess', errorKey: 'services.actions.sendKVAError' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'send'}>
                      {i18n('services.actions.sendKVA')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={i18n('services.actions.declineKVATitle')}
                    description={i18n('services.actions.declineKVAConfirm')}
                    okText={i18n('common.decline')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'decline', successKey: 'services.actions.declineKVASuccess', errorKey: 'services.actions.declineKVAError' })}
                  >
                    <Button danger disabled={!!pendingAction} loading={pendingAction === 'decline'}>
                      {i18n('services.actions.declineKVA')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {watchService.status === 'inProgress' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.requestApprovalTitle')}
                    description={i18n('services.actions.requestApprovalConfirm')}
                    okText={i18n('services.actions.requestApprovalBtn')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'approval', successKey: 'services.actions.requestApprovalSuccess' })}
                  >
                    <Tooltip title={!images.find(img => img.type === 'final') ? i18n('services.actions.requestApprovalHint') : null}>
                      <Button type="primary" disabled={!!pendingAction || !images.find(img => img.type === 'final')} loading={pendingAction === 'approval'}>
                        {i18n('services.actions.requestApproval')}
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Popconfirm
                    title={i18n('services.actions.finishDirectTitle')}
                    description={i18n('services.actions.finishDirectConfirm')}
                    okText={i18n('services.actions.finishDirectBtn')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'finish', successKey: 'services.actions.finishDirectSuccess', checkInvoice: true })}
                  >
                    <Button disabled={!!pendingAction} loading={pendingAction === 'finish'}>
                      {i18n('services.actions.finishDirect')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
              {watchService.status === 'watchReceived' && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Popconfirm
                    title={i18n('services.actions.sendOfferTitle')}
                    description={i18n('services.actions.sendOfferConfirm')}
                    okText={i18n('services.actions.sendOfferBtn')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() => runStatusAction({ path: 'sendOffer', successKey: 'services.actions.sendOfferSuccess', errorKey: 'services.actions.sendKVAError' })}
                  >
                    <Button type="primary" disabled={!!pendingAction} loading={pendingAction === 'sendOffer'}>
                      {i18n('services.actions.sendOffer')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={i18n('services.actions.declineOfferTitle')}
                    description={i18n('services.actions.declineOfferConfirm')}
                    okText={i18n('common.decline')}
                    cancelText={i18n('common.cancel')}
                    onConfirm={() =>
                      runStatusAction({
                        path: 'declineOffer',
                        successKey: 'services.actions.declineOfferSuccess',
                        errorKey: 'services.actions.declineKVAError',
                      })
                    }
                  >
                    <Button danger disabled={!!pendingAction} loading={pendingAction === 'declineOffer'}>
                      {i18n('services.actions.declineOffer')}
                    </Button>
                  </Popconfirm>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      {/* HOR-80: Abbruch-Dialog – Pflichtgrund (1-1000 Zeichen), geht per E-Mail an den Kunden.
          Eigenstaendiges Modal neben dem Detail, damit es dessen destroyOnHidden nicht mitreisst. */}
      <Modal
        title={i18n('services.cancelAction.title')}
        open={cancelModalOpen}
        okText={i18n('services.cancelAction.confirm')}
        okButtonProps={{ danger: true, disabled: !cancelReason.trim() || cancelReason.trim().length > 1000 }}
        cancelText={i18n('common.cancel')}
        confirmLoading={pendingAction === 'cancel'}
        onOk={submitCancel}
        onCancel={() => {
          if (pendingAction === 'cancel') return

          setCancelModalOpen(false)
          setCancelReason('')
        }}
        destroyOnHidden
      >
        <Typography.Paragraph>
          {i18n(CANCEL_RETURN_STATUSES.includes(watchService?.status) ? 'services.cancelAction.hintReturn' : 'services.cancelAction.hintDirect')}{' '}
          {i18n('services.cancelAction.irreversible')}
        </Typography.Paragraph>
        <Typography.Text strong>{i18n('services.cancelAction.reasonLabel')}</Typography.Text>
        <TextArea
          style={{ marginTop: 8 }}
          rows={4}
          maxLength={1000}
          showCount
          autoFocus
          value={cancelReason}
          onChange={e => setCancelReason(e.target.value)}
          placeholder={i18n('services.cancelAction.reasonPlaceholder')}
        />
      </Modal>
    </Page>
  )
}

const formatter = value => <CountUp end={value} separator="." decimal="," decimals={2} suffix=" EUR" />

const getShortCondition = condition => i18n(`services.condition.${condition}`) || condition
